// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useFileStore } from '@/store/file.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  IFileImage,
  IUpdateFileMutationPayload,
} from '@/types/file.types';

/**
 * Hook pour gérer toutes les mutations liées aux produits.
 * Fournit des fonctions pour créer, mettre à jour, supprimer un produit,
 * gérer les images et les recommandations.
 */
export const FileMutation = () => {
  const fileStore = useFileStore();

  // -------------------------------
  // 🔹 Gestion centralisée des erreurs
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0] || 'Une erreur est survenue';

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Upload d'image pour  toute chose dasn le commerce
  // -------------------------------
  const uploadImage = useMutation<any, Error, IFileImage>({
    mutationFn: (payload) => fileStore.uploadImage(payload),
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un fichier
  // -------------------------------
  const UpdateFileDetails = useMutation<any, Error, any>({
    mutationFn: ({ data, fileId }) => fileStore.UpdateFileDetails(data, fileId),
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un fichier
  // -------------------------------
  const deleteFile = useMutation<any, Error, string>({
    mutationFn: (fileId) => fileStore.deleteFile(fileId),
    onSuccess: () => {
      isLoaderLoginFormActivated.value = true;
    },
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  return {
    handleUploadFile: (item: IFileImage) => uploadImage.mutateAsync(item),
    handleUpdateFileDetail: (item: IUpdateFileMutationPayload) =>
      UpdateFileDetails.mutateAsync(item),
    handleDeleteFile: (item: string) => deleteFile.mutateAsync(item),
  };
};
